import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { bulkDelete } from "./_shared";

const EMPTY = { code: "", discount_type: "percent", discount_value: 10, min_order: 0, active: true };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState(EMPTY);
  const [selected, setSelected] = useState(new Set());

  const reload = useCallback(
    () => adminApi().get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {}),
    [],
  );
  useEffect(() => { reload(); }, [reload]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await adminApi().post("/admin/coupons", newCoupon);
      toast.success("Coupon created");
      setNewCoupon(EMPTY);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-6" data-testid="admin-coupons-tab">
      <form onSubmit={submit} className="bg-white border border-[#E5DBD8] p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <label className="flex flex-col"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Code</span><input required value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} className="mt-1 border border-[#E5DBD8] px-3 py-2"/></label>
        <label className="flex flex-col"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Type</span>
          <select value={newCoupon.discount_type} onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })} className="mt-1 border border-[#E5DBD8] px-3 py-2">
            <option value="percent">%</option><option value="flat">Flat ₹</option>
          </select>
        </label>
        <label className="flex flex-col"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Value</span><input type="number" value={newCoupon.discount_value} onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: Number(e.target.value) })} className="mt-1 border border-[#E5DBD8] px-3 py-2"/></label>
        <label className="flex flex-col"><span className="text-xs uppercase tracking-wider text-[#5C5555]">Min Order ₹</span><input type="number" value={newCoupon.min_order} onChange={(e) => setNewCoupon({ ...newCoupon, min_order: Number(e.target.value) })} className="mt-1 border border-[#E5DBD8] px-3 py-2"/></label>
        <button type="submit" className="btn-primary">Add</button>
      </form>
      <div className="mt-4 space-y-2">
        {coupons.length > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-2 bg-[#F2E8E4]/50 border border-[#E5DBD8] px-3 py-2 rounded">
            <label className="inline-flex items-center gap-2 text-sm font-hand cursor-pointer">
              <input
                type="checkbox"
                checked={selected.size > 0 && selected.size === coupons.length}
                onChange={(e) => setSelected(e.target.checked ? new Set(coupons.map((c) => c.id)) : new Set())}
                data-testid="coupons-select-all"
                className="w-4 h-4 accent-[#7A1F2B] cursor-pointer"
              />
              Select all
            </label>
            <div className="text-xs text-[#5C5555]">{selected.size} selected</div>
            <div className="flex gap-2">
              {selected.size > 0 && (
                <button type="button" onClick={() => setSelected(new Set())} className="text-xs px-3 py-1.5 border border-[#E5DBD8] hover:bg-white rounded">Clear</button>
              )}
              <button
                type="button"
                disabled={selected.size === 0}
                onClick={() => bulkDelete("/admin/coupons/delete-bulk", Array.from(selected), reload, setSelected, "coupons")}
                data-testid="coupons-bulk-delete"
                className="text-xs px-3 py-1.5 bg-[#7A1F2B] text-white rounded hover:bg-[#5C1520] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5"/> Delete selected
              </button>
            </div>
          </div>
        )}
        {coupons.map((c) => {
          const checked = selected.has(c.id);
          return (
            <div key={c.id} className={`flex justify-between items-center bg-white border p-3 ${checked ? "border-[#7A1F2B]" : "border-[#E5DBD8]"}`}>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setSelected((prev) => { const n = new Set(prev); if (e.target.checked) n.add(c.id); else n.delete(c.id); return n; })}
                  data-testid={`coupon-check-${c.id}`}
                  className="w-4 h-4 accent-[#7A1F2B] cursor-pointer"
                />
                <div>
                  <span className="font-serif text-lg text-[#7A1F2B]">{c.code}</span>
                  <span className="text-sm text-[#5C5555] ml-3">{c.discount_type === "percent" ? `${c.discount_value}% off` : `₹${c.discount_value} off`} · Min ₹{c.min_order}</span>
                </div>
              </div>
              <button onClick={async () => { await adminApi().delete(`/admin/coupons/${c.id}`); reload(); }} className="text-[#7A1F2B]"><Trash2 className="w-4 h-4"/></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
